import React, { useEffect, useState } from "react";
import CashEntryServices from "../../../services/CashEntryServices";
import CashReturnedCashInEditModal from "./CashReturnedCashInEditModal";
import CashReturnedCashOutEditModal from "./CashReturnedCashOutEditModal";
import Swal from "sweetalert2";
import Loader from "../Loader/loader";
import Pagination from "../Pagenation/Pagenation";

const readEntries = (response) => {
    const body = response?.data?.status
        ? response.data
        : response;

    const entries = body?.data?.entries;

    if (!Array.isArray(entries)) {
        throw new Error("Unexpected returned entries response.");
    }

    return entries;
};

const getId = (value) =>
    String(value?._id || value?.value || value || "").trim();

const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const formatAmount = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const CashReturnedEntriesPanel = ({
    passbookId,
    storeId,
    entryType = "in"
}) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [editingEntry, setEditingEntry] = useState(null);
    const [activePage, setActivePage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const selectedStoreId = getId(storeId);
    const selectedPassbookId = getId(passbookId);

    const totalPages = Math.max(
        1,
        Math.ceil(entries.length / pageSize)
    );

    const currentPage = Math.min(activePage, totalPages - 1);
    const startIndex = currentPage * pageSize;

    const paginatedEntries = entries.slice(
        startIndex,
        startIndex + pageSize
    );

    useEffect(() => {
        setActivePage(0);
        setEditingEntry(null);
    }, [selectedStoreId, selectedPassbookId, entryType]);

    const handleSaved = () => {
        setEditingEntry(null);
        setRefreshKey((previous) => previous + 1);
    };

    useEffect(() => {
        let cancelled = false;

        setEntries([]);

        if (!passbookId || !storeId) {
            setLoading(false);

            return () => {
                cancelled = true;
            };
        }

        const loadEntries = async () => {
            setLoading(true);

            try {
                const response = await CashEntryServices.getCashEntries({
                    storeId: getId(storeId),
                    passbookId: getId(passbookId),
                    entryType,
                    status: "returnedToMaker",
                    pendingOnly: false
                });


                const returnedEntries = readEntries(response)
                    .filter(
                        (entry) =>
                            entry.status === "returnedToMaker" &&
                            entry.entryType === entryType &&
                            getId(entry.passbook) === getId(passbookId)
                    )
                    .sort((first, second) => {
                        const firstDate = new Date(
                            first.returnedAt || first.updatedAt
                        ).getTime();

                        const secondDate = new Date(
                            second.returnedAt || second.updatedAt
                        ).getTime();

                        return (secondDate || 0) - (firstDate || 0);
                    });

                if (!cancelled) {
                    setEntries(returnedEntries);
                }
            } catch (loadError) {
                if (!cancelled) {
                    Swal.fire({
                        icon: "error",
                        title: "Unable to Load Returned Entries",
                        text:
                            loadError?.response?.data?.message ||
                            loadError?.message ||
                            "Please try again.",
                        confirmButtonColor: "#dc3545"
                    });
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadEntries();

        return () => {
            cancelled = true;
        };
    }, [storeId, passbookId, entryType, refreshKey]);

    if (!storeId || !passbookId) {
        return (
            <div className="alert alert-info mb-0">
                Select a Store / Location with an active passbook
                to view returned entries.
            </div>
        );
    }

    return (
        <div className="border rounded p-3 bg-white">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h6 className="mb-0">
                    Returned Cash {entryType === "in" ? "In" : "Out"}
                    {!loading && ` (${entries.length})`}
                </h6>

                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={loading}
                    onClick={() =>
                        setRefreshKey((previous) => previous + 1)
                    }
                >
                    {loading ? "Loading..." : "Refresh"}
                </button>
            </div>
            {loading ? (
                <div
                    className="py-4"
                    role="status"
                    aria-label="Loading returned entries"
                >
                    <Loader />
                </div>
            ) : entries.length === 0 ? (
                <div className="alert alert-info mb-0">
                    No returned Cash {entryType === "in" ? "In" : "Out"} entries found.
                </div>
            ) : (
                <div>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <label
                            htmlFor={`returned-page-size-${entryType}`}
                            className="mb-0 small"
                        >
                            Show
                        </label>

                        <select
                            id={`returned-page-size-${entryType}`}
                            className="form-control"
                            value={pageSize}
                            onChange={(event) => {
                                setPageSize(Number(event.target.value));
                                setActivePage(0);
                            }}
                            style={{ width: 80 }}
                        >
                            {[10, 20, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>

                        <span className="small">entries</span>
                    </div>

                    <div
                        className="table-responsive"
                        style={{
                            maxHeight: 550,
                            overflow: "auto",
                            borderRadius: 12
                        }}
                    >
                        <table
                            className="table table-bordered table-striped align-middle mb-0"
                            style={{ minWidth: 1200 }}
                        >
                            <thead
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                    background: "#f5f5f5"
                                }}
                            >
                                <tr>
                                    <th className="text-nowrap">S.No</th>
                                    <th className="text-nowrap">Request No.</th>
                                    <th className="text-nowrap">Date &amp; Time</th>
                                    <th style={{ minWidth: 300 }}>Details</th>
                                    <th>Type</th>
                                    <th style={{ minWidth: 180 }}>Category</th>
                                    <th>Mode</th>
                                    <th className="text-center">Bills</th>
                                    <th className="text-end">Amount</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginatedEntries.map((entry, index) => {
                                    const reviewer =
                                        entry.returnedBy?.name ||
                                        (
                                            entry.returnedFrom === "Accounts"
                                                ? entry.accountsReviewedBy?.name
                                                : entry.adminReviewedBy?.name
                                        );

                                    const attachments = Array.isArray(entry.image)
                                        ? entry.image
                                        : [];

                                    return (
                                        <tr key={entry._id}>
                                            <td>{startIndex + index + 1}</td>

                                            <td style={{ minWidth: 190 }}>
                                                <div
                                                    className="mb-2"
                                                    style={{ color: "#20c4cb" }}
                                                >
                                                    {entry.requestNumber || "—"}
                                                </div>

                                                <span
                                                    className="badge"
                                                    style={{
                                                        background: "#70a0b8",
                                                        color: "#ffffff"
                                                    }}
                                                >
                                                    Returned to CashMaker
                                                </span>
                                            </td>

                                            <td style={{ minWidth: 150 }}>
                                                {formatDate(entry.dateTime)}
                                            </td>

                                            <td
                                                style={{
                                                    whiteSpace: "normal",
                                                    overflowWrap: "anywhere"
                                                }}
                                            >
                                                <div className="mb-2">
                                                    <strong>Returned By:</strong>{" "}
                                                    {reviewer
                                                        ? `${reviewer} (${entry.returnedFrom || "—"})`
                                                        : entry.returnedFrom || "—"}
                                                </div>

                                                <div
                                                    className="mb-2"
                                                    style={{ whiteSpace: "pre-wrap" }}
                                                >
                                                    {entry.remark || "—"}
                                                </div>

                                                <div className="mb-2">
                                                    By {entry.employee?.name || "—"}
                                                </div>

                                                <details>
                                                    <summary
                                                        style={{
                                                            color: "#0d6efd",
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        View Details
                                                    </summary>

                                                    <div className="border rounded p-2 mt-2 bg-white">
                                                        <div className="mb-2">
                                                            <strong>Returned At:</strong>{" "}
                                                            {formatDate(entry.returnedAt)}
                                                        </div>

                                                        <div
                                                            className="mb-2"
                                                            style={{ whiteSpace: "pre-wrap" }}
                                                        >
                                                            <strong>Return Reason:</strong>{" "}
                                                            {entry.returnReason || "—"}
                                                        </div>

                                                        <div className="mb-2">
                                                            <strong>Voucher / Transaction ID:</strong>{" "}
                                                            {entry.voucherNo || "—"}
                                                        </div>

                                                        {entry.returnWasBypass && (
                                                            <span className="badge bg-warning text-dark">
                                                                CashChecker Bypassed
                                                            </span>
                                                        )}
                                                    </div>
                                                </details>
                                            </td>

                                            <td className="text-nowrap">
                                                <span
                                                    className="badge"
                                                    style={{
                                                        background:
                                                            entry.entryType === "in"
                                                                ? "#58df62"
                                                                : "#ffa34d",
                                                        color:
                                                            entry.entryType === "in"
                                                                ? "#ffffff"
                                                                : "#75400e",
                                                        borderRadius: 20,
                                                        padding: "8px 14px"
                                                    }}
                                                >
                                                    {entry.entryType === "in"
                                                        ? "Cash In"
                                                        : "Cash Out"}
                                                </span>
                                            </td>

                                            <td>{entry.category?.name || "—"}</td>

                                            <td>
                                                {entry.paymentMode === "cash"
                                                    ? "Cash"
                                                    : entry.paymentMode === "online"
                                                        ? "Online"
                                                        : "—"}
                                            </td>

                                            <td className="text-center">
                                                {attachments.length > 0
                                                    ? `${attachments.length} file${attachments.length === 1 ? "" : "s"
                                                    }`
                                                    : "—"}
                                            </td>

                                            <td className="text-end text-nowrap">
                                                ₹{formatAmount(entry.amount)}
                                            </td>

                                            <td className="text-center">
                                                {["in", "out"].includes(entry.entryType) && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => setEditingEntry(entry)}
                                                        title={`Edit returned Cash ${entry.entryType === "in" ? "In" : "Out"
                                                            } entry`}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                        <div className="dataTables_info">
                            Showing {startIndex + 1} to{" "}
                            {Math.min(
                                startIndex + pageSize,
                                entries.length
                            )} of{" "}
                            {entries.length} entries
                        </div>

                        <Pagination
                            activePage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => {
                                if (
                                    !Number.isInteger(page) ||
                                    page < 0 ||
                                    page >= totalPages
                                ) {
                                    return;
                                }

                                setActivePage(page);
                            }}
                        />
                    </div>
                </div>
            )}

            {editingEntry?.entryType === "in" && (
                <CashReturnedCashInEditModal
                    show
                    entry={editingEntry}
                    onHide={() => setEditingEntry(null)}
                    onSaved={handleSaved}
                />
            )}

            {editingEntry?.entryType === "out" && (
                <CashReturnedCashOutEditModal
                    show
                    entry={editingEntry}
                    onHide={() => setEditingEntry(null)}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
};

export default CashReturnedEntriesPanel;